import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

// Glitch text effect
export function glitchText(element: HTMLElement): gsap.core.Timeline {
  const tl = gsap.timeline({ repeat: -1, repeatDelay: 3 });

  tl.to(element, {
    duration: 0.1,
    skewX: 20,
    ease: 'power4.inOut'
  })
    .to(element, {
      duration: 0.04,
      skewX: 0,
      ease: 'power4.inOut'
    })
    .to(element, {
      duration: 0.04,
      opacity: 0.8
    })
    .to(element, {
      duration: 0.04,
      opacity: 1
    })
    .to(element, {
      duration: 0.1,
      skewX: -20,
      ease: 'power4.inOut'
    })
    .to(element, {
      duration: 0.04,
      skewX: 0,
      ease: 'power4.inOut'
    });

  return tl;
}

// Typewriter effect
export function typewriter(
  element: HTMLElement,
  text: string,
  speed: number = 50
): gsap.core.Tween {
  const chars = text.split('');
  element.textContent = '';

  return gsap.to(element, {
    duration: chars.length * (speed / 1000),
    text: {
      value: text,
      delimiter: ''
    },
    ease: 'none'
  });
}

// Fade up animation
export function fadeUp(elements: string | HTMLElement | HTMLElement[]): void {
  gsap.fromTo(
    elements,
    {
      opacity: 0,
      y: 50
    },
    {
      opacity: 1,
      y: 0,
      duration: 0.8,
      stagger: 0.1,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: elements,
        start: 'top 80%',
        toggleActions: 'play none none reverse'
      }
    }
  );
}

// Stagger fade in
export function staggerFadeIn(
  container: string | HTMLElement,
  children: string
): void {
  const containerEl =
    typeof container === 'string'
      ? document.querySelector(container)
      : container;

  if (!containerEl) return;

  gsap.fromTo(
    containerEl.querySelectorAll(children),
    {
      opacity: 0,
      y: 30
    },
    {
      opacity: 1,
      y: 0,
      duration: 0.6,
      stagger: 0.15,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: containerEl,
        start: 'top 75%',
        toggleActions: 'play none none reverse'
      }
    }
  );
}

// Header scroll animation
export function headerScrollAnimation(header: HTMLElement): void {
  ScrollTrigger.create({
    start: 'top top',
    end: 99999,
    onUpdate: (self) => {
      if (self.direction === 1 && self.scroll() > 100) {
        gsap.to(header, {
          y: -100,
          duration: 0.3,
          ease: 'power2.inOut'
        });
      } else {
        gsap.to(header, {
          y: 0,
          duration: 0.3,
          ease: 'power2.inOut'
        });
      }

      if (self.scroll() > 50) {
        header.classList.add('bg-primary/95', 'backdrop-blur-lg', 'shadow-lg');
      } else {
        header.classList.remove(
          'bg-primary/95',
          'backdrop-blur-lg',
          'shadow-lg'
        );
      }
    }
  });
}

// Button hover effects
export function buttonHoverEffects(): void {
  document.querySelectorAll('.btn-primary, .btn-secondary').forEach((btn) => {
    const button = btn as HTMLElement;

    button.addEventListener('mouseenter', () => {
      gsap.to(button, {
        scale: 1.05,
        duration: 0.3,
        ease: 'power2.out'
      });
    });

    button.addEventListener('mouseleave', () => {
      gsap.to(button, {
        scale: 1,
        duration: 0.3,
        ease: 'power2.out'
      });
    });
  });
}

// Card hover effects
export function cardHoverEffects(): void {
  document.querySelectorAll('.cyber-card').forEach((card) => {
    const cardEl = card as HTMLElement;

    cardEl.addEventListener('mouseenter', () => {
      gsap.to(cardEl, {
        y: -5,
        duration: 0.3,
        ease: 'power2.out'
      });
    });

    cardEl.addEventListener('mouseleave', () => {
      gsap.to(cardEl, {
        y: 0,
        duration: 0.3,
        ease: 'power2.out'
      });
    });
  });
}

// Scanner line effect for buttons
export function scannerLineEffect(button: HTMLElement): void {
  const scanLine = document.createElement('div');
  scanLine.className =
    'absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden';
  scanLine.innerHTML = `
    <div class="scanner-line absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white to-transparent opacity-50"></div>
  `;

  button.style.position = 'relative';
  button.appendChild(scanLine);

  const line = scanLine.querySelector('.scanner-line') as HTMLElement;

  gsap.to(line, {
    y: button.offsetHeight,
    duration: 1.5,
    repeat: -1,
    ease: 'none'
  });
}

// Counter animation
export function animateCounter(
  element: HTMLElement,
  endValue: number,
  duration: number = 2
): void {
  const obj = { value: 0 };

  gsap.to(obj, {
    value: endValue,
    duration: duration,
    ease: 'power2.out',
    scrollTrigger: {
      trigger: element,
      start: 'top 80%',
      toggleActions: 'play none none none'
    },
    onUpdate: () => {
      element.textContent = Math.round(obj.value).toLocaleString();
    }
  });
}

// Parallax effect
export function parallax(
  element: string | HTMLElement,
  speed: number = 0.5
): void {
  gsap.to(element, {
    yPercent: -50 * speed,
    ease: 'none',
    scrollTrigger: {
      trigger: element,
      start: 'top bottom',
      end: 'bottom top',
      scrub: true
    }
  });
}

// Timeline animation for roadmap
export function roadmapAnimation(container: HTMLElement): void {
  const cards = container.querySelectorAll('.roadmap-card');

  cards.forEach((card, index) => {
    gsap.fromTo(
      card,
      {
        opacity: 0,
        x: index % 2 === 0 ? -50 : 50
      },
      {
        opacity: 1,
        x: 0,
        duration: 0.8,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: card,
          start: 'top 80%',
          toggleActions: 'play none none reverse'
        }
      }
    );
  });
}

// Steps animation for How It Works
export function stepsAnimation(container: HTMLElement): void {
  const steps = container.querySelectorAll('.step-item');

  steps.forEach((step, index) => {
    const stepNumber = step.querySelector('.step-number');

    gsap.fromTo(
      step,
      {
        opacity: 0,
        x: index % 2 === 0 ? -30 : 30
      },
      {
        opacity: 1,
        x: 0,
        duration: 0.6,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: step,
          start: 'top 80%',
          toggleActions: 'play none none reverse'
        }
      }
    );

    if (stepNumber) {
      gsap.fromTo(
        stepNumber,
        {
          scale: 0,
          rotation: -180
        },
        {
          scale: 1,
          rotation: 0,
          duration: 0.6,
          ease: 'back.out(1.7)',
          scrollTrigger: {
            trigger: step,
            start: 'top 80%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    }
  });
}

// Page load animation
export function pageLoadAnimation(): gsap.core.Timeline {
  const tl = gsap.timeline();

  tl.from('#header', {
    y: -100,
    opacity: 0,
    duration: 0.8,
    ease: 'power3.out'
  })
    .from(
      '#home .heading-xl',
      {
        opacity: 0,
        y: 50,
        duration: 1,
        ease: 'power3.out'
      },
      '-=0.4'
    )
    .from(
      '#home .body-lg',
      {
        opacity: 0,
        y: 30,
        duration: 0.8,
        ease: 'power3.out'
      },
      '-=0.6'
    )
    .from(
      '#home button',
      {
        opacity: 0,
        y: 20,
        stagger: 0.1,
        duration: 0.6,
        ease: 'power3.out'
      },
      '-=0.4'
    );

  return tl;
}

// Initialize all scroll animations
export function initScrollAnimations(): void {
  // Fade up elements
  document.querySelectorAll('.fade-up').forEach((el) => {
    gsap.fromTo(
      el,
      {
        opacity: 0,
        y: 40
      },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
          toggleActions: 'play none none reverse'
        }
      }
    );
  });

  // Feature cards
  const featureCards = document.querySelectorAll('.feature-card');
  if (featureCards.length) {
    gsap.fromTo(
      featureCards,
      {
        opacity: 0,
        y: 50,
        rotateX: 10
      },
      {
        opacity: 1,
        y: 0,
        rotateX: 0,
        duration: 0.8,
        stagger: 0.1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '#features',
          start: 'top 70%',
          toggleActions: 'play none none reverse'
        }
      }
    );
  }

  // Team cards
  const teamCards = document.querySelectorAll('.team-card');
  if (teamCards.length) {
    gsap.fromTo(
      teamCards,
      {
        opacity: 0,
        scale: 0.9
      },
      {
        opacity: 1,
        scale: 1,
        duration: 0.6,
        stagger: 0.1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: '#team',
          start: 'top 70%',
          toggleActions: 'play none none reverse'
        }
      }
    );
  }

  // Social icons
  const socialIcons = document.querySelectorAll('.social-icon');
  if (socialIcons.length) {
    gsap.fromTo(
      socialIcons,
      {
        opacity: 0,
        y: 30
      },
      {
        opacity: 1,
        y: 0,
        duration: 0.5,
        stagger: 0.1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: '#community',
          start: 'top 70%',
          toggleActions: 'play none none reverse'
        }
      }
    );
  }
}

// Smooth scroll to section
export function smoothScrollTo(target: string): void {
  const element = document.querySelector(target);
  if (element) {
    gsap.to(window, {
      duration: 1,
      scrollTo: { y: target, offsetY: 80 },
      ease: 'power3.inOut'
    });
  }
}

// Refresh ScrollTrigger
export function refreshScrollTrigger(): void {
  ScrollTrigger.refresh();
}

// Custom cursor effect
export function initCustomCursor(): void {
  const cursorGlow = document.getElementById('cursor-glow');
  const cursorDot = document.getElementById('cursor-dot');

  if (!cursorGlow || !cursorDot) return;

  let mouseX = 0;
  let mouseY = 0;
  let glowX = 0;
  let glowY = 0;

  // Track mouse position
  document.addEventListener('mousemove', (e) => {
    mouseX = e.clientX;
    mouseY = e.clientY;

    // Update dot position immediately
    cursorDot.style.left = mouseX + 'px';
    cursorDot.style.top = mouseY + 'px';
  });

  // Smooth glow follow
  function animateGlow() {
    glowX += (mouseX - glowX) * 0.1;
    glowY += (mouseY - glowY) * 0.1;

    if (cursorGlow) {
      cursorGlow.style.left = glowX + 'px';
      cursorGlow.style.top = glowY + 'px';
    }

    requestAnimationFrame(animateGlow);
  }
  animateGlow();

  // Add hover effect to interactive elements
  const interactiveElements = document.querySelectorAll('a, button, .cyber-card, .faq-toggle, .social-icon');

  interactiveElements.forEach((el) => {
    el.addEventListener('mouseenter', () => {
      cursorDot.classList.add('hovering');
    });

    el.addEventListener('mouseleave', () => {
      cursorDot.classList.remove('hovering');
    });
  });

  // Hide cursor when leaving window
  document.addEventListener('mouseleave', () => {
    gsap.to([cursorGlow, cursorDot], { opacity: 0, duration: 0.3 });
  });

  document.addEventListener('mouseenter', () => {
    gsap.to([cursorGlow, cursorDot], { opacity: 1, duration: 0.3 });
  });
}

// Card mouse tracking for glow effect
export function initCardMouseTracking(): void {
  const cards = document.querySelectorAll('.cyber-card');

  cards.forEach((card) => {
    const cardEl = card as HTMLElement;

    cardEl.addEventListener('mousemove', (e) => {
      const rect = cardEl.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;

      cardEl.style.setProperty('--mouse-x', x + '%');
      cardEl.style.setProperty('--mouse-y', y + '%');
    });
  });
}

// Magnetic button effect
export function initMagneticButtons(): void {
  const magneticElements = document.querySelectorAll('.btn-primary, .btn-secondary');

  magneticElements.forEach((el) => {
    const element = el as HTMLElement;
    element.classList.add('magnetic');

    element.addEventListener('mousemove', (e) => {
      const rect = element.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const deltaX = (e.clientX - centerX) * 0.2;
      const deltaY = (e.clientY - centerY) * 0.2;

      gsap.to(element, {
        x: deltaX,
        y: deltaY,
        duration: 0.3,
        ease: 'power2.out'
      });
    });

    element.addEventListener('mouseleave', () => {
      gsap.to(element, {
        x: 0,
        y: 0,
        duration: 0.5,
        ease: 'elastic.out(1, 0.3)'
      });
    });
  });
}

// Ripple effect on click
export function initRippleEffect(): void {
  const rippleElements = document.querySelectorAll('.btn-primary, .btn-secondary, .cyber-card');

  rippleElements.forEach((el) => {
    const element = el as HTMLElement;
    element.style.position = 'relative';
    element.style.overflow = 'hidden';

    element.addEventListener('click', (e) => {
      const rect = element.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const ripple = document.createElement('span');
      ripple.className = 'ripple';
      ripple.style.left = x + 'px';
      ripple.style.top = y + 'px';

      element.appendChild(ripple);

      setTimeout(() => {
        ripple.remove();
      }, 600);
    });
  });
}

// Text scramble effect
export function textScramble(element: HTMLElement, finalText: string): void {
  const chars = '!<>-_\\/[]{}—=+*^?#________';
  let iteration = 0;
  const originalText = finalText;

  const interval = setInterval(() => {
    element.textContent = originalText
      .split('')
      .map((_char, index) => {
        if (index < iteration) {
          return originalText[index];
        }
        return chars[Math.floor(Math.random() * chars.length)];
      })
      .join('');

    if (iteration >= originalText.length) {
      clearInterval(interval);
    }

    iteration += 1 / 3;
  }, 30);
}

// Initialize all interactive effects
export function initInteractiveEffects(): void {
  initCustomCursor();
  initCardMouseTracking();
  initMagneticButtons();
  initRippleEffect();
}
